import React, { useState } from 'react';
import {
Box,
Container,
Typography,
Grid,
Card,
CardContent,
TextField,
Button,
Paper,
Chip,
Divider,
useTheme,
useMediaQuery,
Stack,
Alert,
Snackbar,
FormControl,
InputLabel,
Select,
MenuItem,
InputAdornment,
Fade,
Slide,
Avatar,
IconButton
} from '@mui/material';
import {
Email as EmailIcon,
Phone as PhoneIcon,
LocationOn as LocationIcon,
Send as SendIcon,
Schedule as ScheduleIcon,
Language as LanguageIcon,
Support as SupportIcon,
Business as BusinessIcon,
School as SchoolIcon,
Person as PersonIcon,
Subject as SubjectIcon,
Message as MessageIcon,
CheckCircle as CheckCircleIcon,
Facebook,
Twitter,
LinkedIn,
Instagram
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const ContactUs = () => {
const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down('md'));
const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    department: '',
    message: ''
});
const [showSuccess, setShowSuccess] = useState(false);
const [errors, setErrors] = useState({});

const departments = [
    'General Inquiry',
    'Technical Support',
    'Academic Programs',
    'Research Collaboration',
    'Partnership Opportunities',
    'Career Opportunities',
    'Media Relations',
    'Other'
];

const contactInfo = [
    {
    icon: <EmailIcon sx={{ fontSize: 32 }} />,
    title: 'Email Us',
    details: [
        'General: info@research-innovations.org',
        'Support: support@research-innovations.org',
        'Academic: academics@research-innovations.org'
    ],
    color: 'primary'
    },
    {
    icon: <PhoneIcon sx={{ fontSize: 32 }} />,
    title: 'Call Us',
    details: [
        'Main: +1 (555) 123-4567',
        'Support: +1 (555) 123-4568',
        'Emergency: +1 (555) 123-4569'
    ],
    color: 'secondary'
    },
    {
    icon: <LocationIcon sx={{ fontSize: 32 }} />,
    title: 'Visit Us',
    details: [
        '123 Innovation Drive',
        'Tech Valley, CA 94305',
        'United States'
    ],
    color: 'success'
    },
    {
    icon: <ScheduleIcon sx={{ fontSize: 32 }} />,
    title: 'Office Hours',
    details: [
        'Monday - Friday: 8:00 AM - 6:00 PM',
        'Saturday: 9:00 AM - 2:00 PM',
        'Sunday: Closed'
    ],
    color: 'info'
    }
];

const teamContacts = [
    {
    name: 'Dr. Sarah Mitchell',
    role: 'Director of Research',
    email: 's.mitchell@research-innovations.org',
    phone: '+1 (555) 123-4570',
    department: 'Research Leadership'
    },
    {
    name: 'Prof. James Chen',
    role: 'Academic Programs Head',
    email: 'j.chen@research-innovations.org',
    phone: '+1 (555) 123-4571',
    department: 'Academic Affairs'
    },
    {
    name: 'Emily Rodriguez',
    role: 'Technical Support Manager',
    email: 'e.rodriguez@research-innovations.org',
    phone: '+1 (555) 123-4572',
    department: 'Technical Support'
    },
    {
    name: 'Michael Thompson',
    role: 'Partnership Director',
    email: 'm.thompson@research-innovations.org',
    phone: '+1 (555) 123-4573',
    department: 'Business Development'
    }
];

const socialLinks = [
    { icon: <Facebook />, name: 'Facebook', url: '#' },
    { icon: <Twitter />, name: 'Twitter', url: '#' },
    { icon: <LinkedIn />, name: 'LinkedIn', url: '#' },
    { icon: <Instagram />, name: 'Instagram', url: '#' }
];

const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
    setErrors(prev => ({ ...prev, [field]: '' }));
    }
};

const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
    newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
    newErrors.email = 'Email is invalid';
    }
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.department) newErrors.department = 'Please select a department';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
};

const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
    console.log('Form submitted:', formData);
    // Here you would typically send the data to your backend
    setFormData({
        name: '',
        email: '',
        subject: '',
        department: '',
        message: ''
    });
    setShowSuccess(true);
    }
};

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
    opacity: 1,
    transition: {
        staggerChildren: 0.1
    }
    }
};

const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
    y: 0,
    opacity: 1,
    transition: {
        duration: 0.5
    }
    }
};

return (
    <Box sx={{ 
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    py: 8
    }}>
    <Container maxWidth="lg">
        {/* Header Section */}
        <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        >
        <Box textAlign="center" mb={8}>
            <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom 
            sx={{ 
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #1976d2, #00bcd4)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent'
            }}
            >
            Get In Touch
            </Typography>
            <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ maxWidth: '600px', mx: 'auto', mb: 2 }}
            >
            We're here to help you with your research and innovation journey. 
            Reach out to us through any of the following channels.
            </Typography>
            <Chip 
            icon={<SupportIcon />} 
            label="24/7 Support Available" 
            color="primary" 
            variant="outlined"
            />
        </Box>
        </motion.div>

        {/* Contact Information Cards */}
        <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        >
        <Grid container spacing={3} sx={{ mb: 8 }}>
            {contactInfo.map((contact, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div variants={itemVariants}>
                <Card 
                    sx={{ 
                    height: '100%',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 6
                    }
                    }}
                >
                    <CardContent sx={{ p: 4 }}>
                    <Box 
                        sx={{ 
                        display: 'inline-flex',
                        p: 2,
                        borderRadius: '50%',
                        bgcolor: `${contact.color}.light`,
                        color: `${contact.color}.main`,
                        mb: 3
                        }}
                    >
                        {contact.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {contact.title}
                    </Typography>
                    {contact.details.map((detail, idx) => (
                        <Typography 
                        key={idx} 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ mb: 1 }}
                        >
                        {detail}
                        </Typography>
                    ))}
                    </CardContent>
                </Card>
                </motion.div>
            </Grid>
            ))}
        </Grid>
        </motion.div>

        {/* Main Content - Form and Map */}
        <Grid container spacing={6}>
        {/* Contact Form */}
        <Grid item xs={12} lg={8}>
            <Slide direction="right" in={true} mountOnEnter unmountOnExit>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <SendIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    Send us a Message
                </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Full Name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        error={!!errors.name}
                        helperText={errors.name}
                        InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                            <PersonIcon color="action" />
                            </InputAdornment>
                        ),
                        }}
                        variant="outlined"
                    />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        error={!!errors.email}
                        helperText={errors.email}
                        InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                            <EmailIcon color="action" />
                            </InputAdornment>
                        ),
                        }}
                        variant="outlined"
                    />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Subject"
                        value={formData.subject}
                        onChange={(e) => handleInputChange('subject', e.target.value)}
                        error={!!errors.subject}
                        helperText={errors.subject}
                        InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                            <SubjectIcon color="action" />
                            </InputAdornment>
                        ),
                        }}
                        variant="outlined"
                    />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!errors.department}>
                        <InputLabel>Department</InputLabel>
                        <Select
                        value={formData.department}
                        onChange={(e) => handleInputChange('department', e.target.value)}
                        label="Department"
                        startAdornment={
                            <InputAdornment position="start">
                            <BusinessIcon color="action" />
                            </InputAdornment>
                        }
                        >
                        {departments.map((dept) => (
                            <MenuItem key={dept} value={dept}>
                            {dept}
                            </MenuItem>
                        ))}
                        </Select>
                        {errors.department && (
                        <Typography variant="caption" color="error">
                            {errors.department}
                        </Typography>
                        )}
                    </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                    <TextField
                        fullWidth
                        multiline
                        rows={6}
                        label="Your Message"
                        value={formData.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        error={!!errors.message}
                        helperText={errors.message}
                        InputProps={{
                        startAdornment: (
                            <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                            <MessageIcon color="action" />
                            </InputAdornment>
                        ),
                        }}
                        variant="outlined"
                        placeholder="Please provide detailed information about your inquiry..."
                    />
                    </Grid>
                    <Grid item xs={12}>
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        endIcon={<SendIcon />}
                        sx={{ 
                        py: 1.5, 
                        px: 4,
                        fontSize: '1.1rem'
                        }}
                    >
                        Send Message
                    </Button>
                    </Grid>
                </Grid>
                </form>
            </Paper>
            </Slide>

            {/* Team Contacts Section */}
            <Fade in={true} timeout={1000}>
            <Box sx={{ mt: 6 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                Key Contacts
                </Typography>
                <Grid container spacing={3}>
                {teamContacts.map((member, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                    <Card sx={{ borderRadius: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                            {member.name.split(' ').map(n => n[0]).join('')}
                            </Avatar>
                            <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {member.name}
                            </Typography>
                            <Typography variant="body2" color="primary.main">
                                {member.role}
                            </Typography>
                            </Box>
                            <Chip 
                                label={member.department} 
                                size="small" 
                                variant="outlined"
                                sx={{ mb: 2 }}
                            />
                            <Stack spacing={1}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2">
                                    {member.email}
                                </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2">
                                    {member.phone}
                                </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    </CardContent>
                    </Card>
                    </Grid>
                ))}
                </Grid>
            </Box>
            </Fade>
        </Grid>

        {/* Sidebar - Map and Additional Info */}
        <Grid item xs={12} lg={4}>
            <Stack spacing={4}>
            {/* Location Map */}
            <Slide direction="left" in={true} mountOnEnter unmountOnExit>
                <Paper elevation={3} sx={{ borderRadius: 4, overflow: 'hidden' }}>
                <Box 
                    sx={{ 
                    height: '300px',
                    background: 'linear-gradient(45deg, #e3f2fd, #bbdefb)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                    }}
                >
                    <LocationIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Research and Innovations Academy
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center">
                    123 Innovation Drive<br />
                    Tech Valley, CA 94305
                    </Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                    <Button 
                    fullWidth 
                    variant="outlined" 
                    startIcon={<LocationIcon />}
                    >
                    Get Directions
                    </Button>
                </Box>
                </Paper>
            </Slide>

            {/* Social Media */}
            <Paper elevation={3} sx={{ p: 4, borderRadius: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Follow Us
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Stay updated with our latest research and innovations
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="center">
                {socialLinks.map((social, index) => (
                    <IconButton
                    key={index}
                    sx={{ 
                        bgcolor: 'primary.light',
                        color: 'primary.main',
                        '&:hover': {
                        bgcolor: 'primary.main',
                        color: 'white'
                        }
                    }}
                    >
                    {social.icon}
                    </IconButton>
                ))}
                </Stack>
            </Paper>

            {/* Quick Stats */}
            <Paper elevation={3} sx={{ p: 4, borderRadius: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Why Choose Us?
                </Typography>
                <Stack spacing={2}>
                {[
                    { label: '24/7 Expert Support', icon: <SupportIcon color="primary" /> },
                    { label: 'Global Research Network', icon: <LanguageIcon color="primary" /> },
                    { label: 'Industry Partnerships', icon: <BusinessIcon color="primary" /> },
                    { label: 'Academic Excellence', icon: <SchoolIcon color="primary" /> }
                ].map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircleIcon sx={{ mr: 2, color: 'success.main' }} />
                    <Typography variant="body2">
                        {item.label}
                    </Typography>
                    </Box>
                ))}
                </Stack>
            </Paper>
            </Stack>
        </Grid>
        </Grid>

        {/* Success Message */}
        <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
        <Alert 
            onClose={() => setShowSuccess(false)} 
            severity="success" 
            icon={<CheckCircleIcon fontSize="inherit" />}
            sx={{ width: '100%' }}
        >
            Thank you for your message! We'll get back to you within 24 hours.
        </Alert>
        </Snackbar>
    </Container>
    </Box>
);
};

export default ContactUs;